import {
  Box,
  Button,
  Flex,
  Link,
  List,
  ListItem,
  Show,
  Text,
} from "@chakra-ui/react";
import ModalDrawer from "@components/ModalDrawer";
import BaseImage from "@components/BaseImage";
import FooterButtons from "@components/SuccessErrorModalBodyTemplate/components/FooterButtons";
import { ClaimType } from "@hooks/useClaims";

interface DisclaimerProps {
  isOpen: boolean;
  onClose: () => void;
  onApprove: () => void;
  type: ClaimType;
}

const ClaimELDisclaimer = () => {
  return (
    <Text
      color="textSecondary"
      fontSize="14px"
      lineHeight="20px"
      fontWeight="400"
    >
      By claiming, using, or transferring EIGEN tokens, you acknowledge and
      agree that EIGEN is issued solely by both the Eigen Foundation and Eigen
      Labs, Inc., and is subject to their respective Terms of Service, available
      at Eigen Foundation{" "}
      <Link
        color="textPrimary"
        fontWeight="600"
        textDecor="underline"
        href="https://docs.eigenfoundation.org/legal/terms-of-service"
        target="_blank"
      >
        Terms of Service
      </Link>{" "}
      and{" "}
      <Link
        color="textPrimary"
        fontWeight="600"
        textDecor="underline"
        href="https://docs.eigenlayer.xyz/eigenlayer/legal/terms-of-service"
        target="_blank"
      >
        Eigen Labs Terms of Service.
      </Link>{" "}
      Kelp DAO solely facilitates access to the claim and disclaims all
      warranties and liabilities regarding the EIGEN tokens, as well as any
      third-party services, including those operated by Eigen Foundation or
      Eigen Labs.
    </Text>
  );
};

const ClaimScrollDisclaimer = () => {
  const renderLink = (text: string, link: string) => {
    return (
      <Link
        color="textPrimary"
        fontWeight="600"
        textDecor="underline"
        href={link}
        target="_blank"
      >
        {text}
      </Link>
    );
  };
  return (
    <Box>
      <Text
        color="textSecondary"
        fontSize="14px"
        lineHeight="20px"
        fontWeight="400"
        textAlign="justify"
      >
        By claiming SCR tokens, you agree and acknowledge that:
      </Text>

      <List
        fontSize="14px"
        lineHeight="20px"
        fontWeight="400"
        styleType="lower-roman"
        padding="0.7rem"
        display="flex"
        flexDirection="column"
        gap="0.5rem"
        textAlign="justify"
      >
        <ListItem>
          You have read and understood the{" "}
          {renderLink(
            "Scroll Airdrop Terms and Conditions",
            "https://scroll.io/files/airdrop/terms-and-conditions.pdf"
          )}
          {", "}
          {renderLink(
            "Scroll Terms of Service",
            "https://scroll.io/terms-of-service"
          )}{" "}
          and{" "}
          {renderLink(
            "Scroll Privacy Policy",
            "https://scroll.io/privacy-policy"
          )}
          , and agree to abide by all of the terms and conditions contained
          therein.
        </ListItem>
        <ListItem>
          Kelp DAO solely facilitates access to the claim and disclaims all
          warranties and liabilities regarding the SCR tokens, as well as any
          third-party services, including those operated by Scroll Foundation.
        </ListItem>
        <ListItem>
          You have read and accepted the Kelp DAO{" "}
          {renderLink(
            "Terms of Service",
            "https://www.kelpdao.xyz/terms-of-service/"
          )}{" "}
          and{" "}
          {renderLink(
            "Privacy Policy",
            "https://www.kelpdao.xyz/privacy-policy/"
          )}
          , and agree to comply with all the terms and conditions therein when
          interacting with the Kelp DAO platform and its services.
        </ListItem>
        <ListItem>
          Scroll Foundation is not responsible for the claims process operated
          by Kelp DAO and disclaims all related liabilities.
        </ListItem>
      </List>
    </Box>
  );
};

const Disclaimer = ({ isOpen, onClose, onApprove, type }: DisclaimerProps) => {
  return (
    <ModalDrawer isOpen={isOpen} closeAlert={onClose}>
      <Flex
        padding={{ base: "24px 16px", md: "40px" }}
        flexDirection="column"
        width="100%"
        gap={{ base: "24px", md: "32px" }}
      >
        <Flex
          justifyContent={"space-between"}
          align={"center"}
          flexDir={{ base: "column-reverse", md: "row" }}
        >
          <Text
            fontSize={{ base: "16px", md: "18px" }}
            lineHeight={"24px"}
            fontWeight={"600"}
          >
            {type === ClaimType.SCROLL ? "Agreement to Terms" : "Disclaimer"}
          </Text>
          <Button
            variant="unstyled"
            display="flex"
            p={{ base: "1.5rem 0", md: 0 }}
            height="24px"
            onClick={onClose}
            _focus={{
              boxShadow: "none",
            }}
          >
            <Show above="md">
              <BaseImage
                height={24}
                width={24}
                alt="close"
                src="/assets/close.svg"
              />
            </Show>
            <Show below="md">
              <BaseImage
                height={4}
                width={20}
                alt="close"
                src="/assets/close_mobile.svg"
              />
            </Show>
          </Button>
        </Flex>
        {type === ClaimType.SCROLL ? (
          <ClaimScrollDisclaimer />
        ) : (
          <ClaimELDisclaimer />
        )}

        <FooterButtons
          secondaryBtnTxt="Cancel"
          onSubmitSecondary={onClose}
          primaryBtnTxt="Agree"
          onSubmitPrimary={onApprove}
        />
      </Flex>
    </ModalDrawer>
  );
};

export default Disclaimer;
